import type { FC } from 'react';
import { Navigate, Route, useParams } from 'react-router';
import { useDocumentExchangeRoot } from 'src/apis/document-exchange/use-document-exchange-root';
import { useDocuments } from 'src/apis/monolith/use-documents';
import type { ShipperPlatformIdsRequest } from 'src/apis/monolith/use-shipper-platform-ids';
import { Card } from 'src/components/data-display/card';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import FromShipperPage from 'src/pages/shippers/details/documents/from-shipper/page';
import { ShipperNavigationBar } from 'src/pages/shippers/details/documents/shipper-navigation-bar';
import { ToShipperPage } from 'src/pages/shippers/details/documents/to-shipper/page';
import { Forbidden } from 'src/utils/errors';

type ShipperContractsProps = {
  shipperName: string;
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
};

export const ShipperDocuments: FC<ShipperContractsProps> = ({
  shipperName,
  shipperPlatformIds,
}) => {
  if (!shipperPlatformIds) throw new Forbidden();

  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  const shipperRefId = shipperPlatformIds.response.data.referenceId.toString();
  const documents = useDocuments({ shipperRefId });
  const documentExchange = useDocumentExchangeRoot();

  if (!documentExchange.response) return <PageSpinner />;

  return (
    <div className="grid grid-flow-col">
      <Card>
        <div className="grid items-center gap-sm">
          <div className="grid grid-flow-col items-center justify-between">
            <Heading mode="headline" size="md">
              Documents
            </Heading>
          </div>
          {documentExchange.response.data._links.getOperatorDocumentsOutbox
            ?.href && <ShipperNavigationBar />}
          <RootRoutes>
            {documentExchange.response.data._links.getOperatorDocumentsOutbox
              ?.href && (
              <Route
                path="/dashboard/shippers/:shipperUuid/documents/to"
                element={<ToShipperPage documentExchange={documentExchange} />}
              />
            )}

            <Route
              path="/dashboard/shippers/:shipperUuid/documents/from"
              element={
                <FromShipperPage
                  shipperName={shipperName}
                  documents={documents}
                  shipperPlatformIds={shipperPlatformIds}
                />
              }
            />

            <Route
              path="/dashboard/shippers/:shipperUuid/documents"
              element={
                <Navigate
                  replace
                  to={`/dashboard/shippers/${shipperUuid}/documents/from`}
                />
              }
            />

            <Route path="*" element={<ThrowNotFound />} />
          </RootRoutes>
        </div>
      </Card>
    </div>
  );
};
